import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '@/store';
import type { Chatbot } from '@/types/chatbot';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { ChatbotService } from '@/api/services/chatbotService';

interface ChatbotsState {
  chatbots: Chatbot[];
  selectedChatbot: Chatbot | null;
  loading: boolean;
  error: string | null;
  hasFetched: boolean;
  lastFetchTime: number | null;
}

const initialState: ChatbotsState = {
  chatbots: [],
  selectedChatbot: null,
  loading: false,
  error: null,
  hasFetched: false,
  lastFetchTime: null,
};

export const fetchChatbots = createAsyncThunk(
  'chatbots/fetchChatbots',
  async (_, { rejectWithValue }) => {
    try {
      const response = await ChatbotService.fetchChatbots();
      console.log(response, 'response')
      return response.data;
    } catch (error) {
      return rejectWithValue('Failed to fetch chatbots');
    }
  }
);

export const fetchChatbot = createAsyncThunk(
  'chatbots/fetchChatbot',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await ChatbotService.getChatbot(id);
      return response;
    } catch (error) {
      return rejectWithValue('Failed to fetch chatbot');
    }
  }
);

export const createChatbot = createAsyncThunk(
  'chatbots/createChatbot',
  async (chatbot: Partial<Chatbot>, { rejectWithValue }) => {
    try {
      const response = await ChatbotService.createChatbot(chatbot);
      console.log(response, 'response')
      const newChatbot: Chatbot = {
        ...chatbot,
        id: response.id || Date.now().toString(),
        status: chatbot.status || 'inactive',
        createdAt: response.createdAt || new Date().toISOString(),
      } as Chatbot;
      
      return newChatbot;
    } catch (error) {
      return rejectWithValue('Failed to create chatbot');
    }
  }
);

export const updateChatbot = createAsyncThunk(
  'chatbots/updateChatbot',
  async ({ id, data }: { id: string; data: Partial<Chatbot> }, { rejectWithValue }) => {
    try {
      const response = await ChatbotService.updateChatbot(id, data);
      return response;
    } catch (error) {
      return rejectWithValue('Failed to update chatbot');
    }
  }
);

// Create the slice
const chatbotsSlice = createSlice({
  name: 'chatbots',
  initialState,
  reducers: {
    setChatbots: (state, action: PayloadAction<Chatbot[]>) => {
      state.chatbots = action.payload;
    },
    clearChatbots: (state) => {
      state.chatbots = [];
    },
    clearSelectedChatbot: (state) => {
      state.selectedChatbot = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchChatbots.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchChatbots.fulfilled, (state, action) => {
        console.log(action.payload, 'action.payload')
        state.chatbots = action.payload;
        state.loading = false;
        state.hasFetched = true;
        state.lastFetchTime = Date.now();
        state.error = null;
      })
      .addCase(fetchChatbots.rejected, (state, action) => {
        state.loading = false;
        // Only set error if it's not a duplicate request
        if (action.payload !== 'Already fetching chatbots' && action.payload !== 'Recently fetched') {
          state.error = action.payload as string;
        }
      })
      .addCase(fetchChatbot.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchChatbot.fulfilled, (state, action) => {
        state.selectedChatbot = action.payload;
        state.loading = false;
      })
      .addCase(fetchChatbot.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createChatbot.fulfilled, (state, action) => {
        state.chatbots.push(action.payload);
        state.loading = false;
      })
      .addCase(createChatbot.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateChatbot.fulfilled, (state, action) => {
        const index = state.chatbots.findIndex(bot => bot.id === action.payload.id);
        if (index !== -1) {
          state.chatbots[index] = action.payload;
        }
        if (state.selectedChatbot?.id === action.payload.id) {
          state.selectedChatbot = action.payload;
        }
        state.loading = false;
      })
      .addCase(updateChatbot.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setChatbots, clearChatbots, clearSelectedChatbot } = chatbotsSlice.actions;

export const selectChatbots = (state: RootState) => state.chatbots.chatbots;
export const selectSelectedChatbot = (state: RootState) => state.chatbots.selectedChatbot?.data;
export const selectChatbotsLoading = (state: RootState) => state.chatbots.loading;
export const selectChatbotsError = (state: RootState) => state.chatbots.error;
export const selectChatbotsHasFetched = (state: RootState) => state.chatbots.hasFetched;

// Helper function to conditionally fetch chatbots
export const fetchChatbotsIfNeeded = () => (dispatch: any, getState: () => RootState) => {
  const state = getState();
  const { loading, hasFetched, lastFetchTime } = state.chatbots;
  
  // Don't fetch if already loading
  if (loading) {
    return;
  }
  
  // Don't refetch if we've fetched within the last 30 seconds
  const now = Date.now();
  if (hasFetched && lastFetchTime && (now - lastFetchTime < 30000)) {
    return;
  }
  
  // Only fetch if we haven't fetched before or it's been a while
  if (!hasFetched || !lastFetchTime || (now - lastFetchTime > 30000)) {
    dispatch(fetchChatbots());
  }
};

export const chatbotsReducer = chatbotsSlice.reducer;
export default chatbotsSlice.reducer;
